import { useRef } from "react";
import { motion, useMotionValue, useTransform, useSpring } from "framer-motion";
import { ArrowUpRight, ArrowDown } from "lucide-react";
import { EASE } from "./Reveal";
import { DashboardMockup } from "./DashboardMockup";
import { scrollToSection } from "../../config/site";

const LINES = ["Building Digital Solutions", "That Move Your", "Business Forward."];

export const Hero = () => {
  const ref = useRef(null);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rX = useSpring(useTransform(my, [-0.5, 0.5], [5, -5]), { stiffness: 120, damping: 18 });
  const rY = useSpring(useTransform(mx, [-0.5, 0.5], [-7, 7]), { stiffness: 120, damping: 18 });

  const onMove = (e) => {
    const rect = ref.current?.getBoundingClientRect();
    if (!rect) return;
    mx.set((e.clientX - rect.left) / rect.width - 0.5);
    my.set((e.clientY - rect.top) / rect.height - 0.5);
  };

  return (
    <section
      id="home"
      ref={ref}
      onMouseMove={onMove}
      className="grain grid-lines relative overflow-hidden bg-white pt-[72px]"
      data-testid="hero-section"
    >
      {/* ambient lights */}
      <div className="pointer-events-none absolute -top-32 right-[8%] h-[420px] w-[420px] rounded-full bg-gold/10 blur-[120px]" />
      <div className="pointer-events-none absolute bottom-0 left-[-6%] h-[380px] w-[380px] rounded-full bg-magenta/[0.07] blur-[120px]" />

      <div className="relative z-10 mx-auto grid max-w-7xl grid-cols-1 items-center gap-16 px-5 pb-24 pt-14 sm:px-8 lg:grid-cols-[1.05fr_0.95fr] lg:pb-32 lg:pt-20">
        <div>
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: EASE }}
            className="inline-flex items-center gap-2.5 rounded-full border border-gold/30 bg-gold-pale px-4 py-1.5"
            data-testid="hero-badge"
          >
            <span className="h-1.5 w-1.5 rounded-full bg-gold pulse-dot" />
            <span className="font-mono2 text-[10px] font-semibold uppercase tracking-[0.3em] text-gold">Rebhas Consultancy Services</span>
          </motion.div>

          <h1 className="mt-7 font-jakarta text-4xl font-extrabold leading-[1.06] tracking-tight text-navy sm:text-5xl lg:text-6xl" data-testid="hero-heading">
            {LINES.map((line, i) => (
              <span key={line} className="mask-line">
                <motion.span
                  initial={{ y: "112%" }}
                  animate={{ y: 0 }}
                  transition={{ duration: 1, delay: 0.15 + i * 0.13, ease: EASE }}
                  className={i === 2 ? "gradient-brand pb-1" : ""}
                >
                  {line}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.65, ease: EASE }}
            className="mt-6 max-w-xl font-inter text-base leading-relaxed text-slate-600 sm:text-lg"
            data-testid="hero-subtext"
          >
            RCS helps businesses, schools and organizations transform their ideas into powerful digital
            products — from custom software and SaaS platforms to websites, mobile apps and AI-powered solutions.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.8, ease: EASE }}
            className="mt-9 flex flex-wrap items-center gap-4"
          >
            <button
              onClick={() => scrollToSection("#contact")}
              data-testid="hero-consultation-cta"
              className="group inline-flex items-center gap-2.5 rounded-full bg-navy px-7 py-4 font-jakarta text-sm font-semibold text-white shadow-[0_16px_40px_-12px_rgba(17,24,39,0.5)] transition-colors duration-300 hover:bg-gold"
            >
              Get a Free Consultation
              <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
            </button>
            <button
              onClick={() => scrollToSection("#services")}
              data-testid="hero-services-cta"
              className="group inline-flex items-center gap-2.5 rounded-full border border-slate-300 bg-white/70 px-7 py-4 font-jakarta text-sm font-semibold text-navy backdrop-blur transition-colors duration-300 hover:border-gold hover:text-gold"
            >
              Explore Our Services
              <ArrowDown className="h-4 w-4 transition-transform duration-300 group-hover:translate-y-0.5" />
            </button>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 1.05 }}
            className="mt-10 flex flex-wrap items-center gap-x-4 gap-y-2 font-mono2 text-[10.5px] uppercase tracking-[0.22em] text-slate-500"
            data-testid="hero-trust-line"
          >
            <span>Custom Solutions</span>
            <span className="h-1 w-1 rounded-full bg-gold" />
            <span>Scalable Technology</span>
            <span className="h-1 w-1 rounded-full bg-magenta" />
            <span>Long-Term Support</span>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 48 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1.1, delay: 0.5, ease: EASE }}
          className="perspective-1200 relative pb-10 pl-4 sm:pl-10 lg:pl-0"
        >
          <motion.div style={{ rotateX: rX, rotateY: rY, transformStyle: "preserve-3d" }}>
            <DashboardMockup />
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};
